import { Alert, AlertIcon, AlertTitle, Checkbox, Divider, useToast, VStack, Select } from '@chakra-ui/react';
import { Api } from 'api';
import { FormControlFormInput, GenericMessages } from 'core';
import { useFormik } from 'formik';
import React, { useMemo, useEffect } from 'react';
import { useMutation } from 'react-query';
import {
  ExpertisePackageAbility,
  ExpertisePackageWithAbilities,
  ExpertisePointGroupType,
  ExpertisePointGroupTypeLabel,
} from 'types';
import * as yup from 'yup';

type AbilityDefinitionType = {
  [key: number]: boolean;
};

const filterAbilityDefinitionsMap = (abilityDefinitionTypes: AbilityDefinitionType) =>
  Object.entries(abilityDefinitionTypes).filter((iter) => iter[1]);

const validationSchema = yup.object({
  name: yup.string().required(GenericMessages.CannotBeEmpty),
  price: yup.number().required(GenericMessages.CannotBeEmpty).min(0, GenericMessages.CantBeLessThanNumber(0)),
  groupType: yup.number().required(GenericMessages.CannotBeEmpty),
  abilityDefinitionTypes: yup
    .object()
    .test('abilityDefinitionTypes', 'En az bir paket özelliği seçilmiş olmalıdır', (value) => {
      return filterAbilityDefinitionsMap(value).length > 0;
    })
    .test('mutualExclusiveAbilities', 'Tekli Araç Fotoğrafı ve Çoklu Araç Fotoğrafı aynı anda seçilemez', (value) => {
      // AracFotografiTekli = 6, AracFotografiCoklu = 7
      const hasSinglePhoto = value[6];
      const hasMultiplePhoto = value[7];
      return !(hasSinglePhoto && hasMultiplePhoto);
    }),
});

type SavePackageWithAbilitiesFormData = {
  id?: number;
  name: string;
  price: string;
  groupType: string;
  abilityDefinitionTypes: AbilityDefinitionType;
  includeInRoyalty?: boolean;
  royaltyRate?: string | undefined;
  royaltyCheckboxChecked?: boolean;
};

export interface AdminPackageAbilitiesFormProps {
  formId: string;
  onSubmitStart: () => void;
  onSubmitComplete: (isSuccess: boolean) => void;
  abilities: ExpertisePackageAbility[];
  packageWithAbilities?: ExpertisePackageWithAbilities;
}

export const AdminPackageAbilitiesForm: React.FC<AdminPackageAbilitiesFormProps> = ({
  formId,
  onSubmitStart,
  onSubmitComplete,
  abilities,
  packageWithAbilities,
}) => {
  const toast = useToast();
  const initialAbilityDefinitionTypeTable = useMemo(() => {
    const freqMap = abilities.reduce((accum, next) => {
      const newAccum = { ...accum };
      newAccum[next.abilityDefinitionTypeId] = false;
      return newAccum;
    }, {} as any);

    if (packageWithAbilities) {
      packageWithAbilities.abilities.forEach((ability) => {
        freqMap[ability.abilityDefinitionTypeId] = true;
      });
    }

    return freqMap;
  }, [abilities]);

  const initialRoyaltyRateChecked = useMemo(() => {
    return packageWithAbilities?.expertisePackage.royaltyRate != null;
  }, [packageWithAbilities?.expertisePackage.royaltyRate]);

  const mutation = useMutation<void, void, SavePackageWithAbilitiesFormData>(
    async (formData: SavePackageWithAbilitiesFormData) => {
      onSubmitStart();
      await Api.Expertise.savePackageWithAbilities({
        id: packageWithAbilities?.expertisePackage.id || 0,
        name: formData.name,
        price: parseInt(formData.price, 10),
        groupType: parseInt(formData.groupType, 10) as ExpertisePointGroupType,
        includeInRoyalty: !formData.includeInRoyalty,
        abilityDefinitionTypes: filterAbilityDefinitionsMap(formData.abilityDefinitionTypes).map((iter) =>
          parseInt(iter[0], 10),
        ),
        royaltyRate:
          !formData.includeInRoyalty && formData.royaltyCheckboxChecked
            ? parseInt(formData.royaltyRate ?? '0', 10)
            : null,
      });
    },
    {
      onSuccess: () => {
        toast({
          title: 'Paket başarı ile kayıt edildi',
          status: 'success',
          isClosable: true,
        });
        onSubmitComplete(true);
      },
      onError: () => {
        toast({
          title: 'Paket kayıt edilirken hata oluştu',
          status: 'error',
          isClosable: true,
        });
        onSubmitComplete(false);
      },
    },
  );
  const { values, errors, handleChange, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      name: packageWithAbilities?.expertisePackage.name || '',
      price: packageWithAbilities?.expertisePackage.price || '',
      groupType: packageWithAbilities?.expertisePackage.groupType?.toString() || '',
      abilityDefinitionTypes: initialAbilityDefinitionTypeTable,
      includeInRoyalty: packageWithAbilities ? !packageWithAbilities.expertisePackage.includeInRoyalty : false,
      // info: ı dont get checked value from backend so i added a new field to check if it is checked or not
      royaltyCheckboxChecked: packageWithAbilities?.expertisePackage.includeInRoyalty && initialRoyaltyRateChecked,
      royaltyRate:
        packageWithAbilities?.expertisePackage.includeInRoyalty && initialRoyaltyRateChecked
          ? packageWithAbilities?.expertisePackage.royaltyRate ?? '0'
          : undefined,
    },
    onSubmit: (formData: SavePackageWithAbilitiesFormData) => {
      mutation.mutate(formData);
    },
    validationSchema,
    enableReinitialize: true,
    validateOnChange: false,
  });

  useEffect(() => {
    const hasSinglePhoto = values.abilityDefinitionTypes[6];
    const hasMultiplePhoto = values.abilityDefinitionTypes[7];

    if (hasSinglePhoto && hasMultiplePhoto) {
      setFieldValue('abilityDefinitionTypes.7', false);
      toast({
        title: 'Uyarı',
        description: 'Tekli Araç Fotoğrafı ve Çoklu Araç Fotoğrafı aynı anda seçilemez. Çoklu fotoğraf kaldırıldı.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  }, []);

  const handleRoyaltyCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setFieldValue('royaltyCheckboxChecked', isChecked);
    setFieldValue('royaltyRate', isChecked ? '0' : undefined);
  };

  const handleAbilityCheckboxChange = (abilityId: number, checked: boolean) => {
    const updatedValues = {
      ...values.abilityDefinitionTypes,
      [abilityId]: checked,
    };

    const hasSinglePhoto = abilityId === 6 ? checked : updatedValues[6];
    const hasMultiplePhoto = abilityId === 7 ? checked : updatedValues[7];

    if (hasSinglePhoto && hasMultiplePhoto) {
      toast({
        title: 'Uyarı',
        description: 'Tekli Araç Fotoğrafı ve Çoklu Araç Fotoğrafı aynı anda seçilemez',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFieldValue(`abilityDefinitionTypes.${abilityId}`, checked);
  };

  return (
    <>
      <form id={formId} noValidate onSubmit={handleSubmit} />
      <VStack align="flex-start" gap={2}>
        <FormControlFormInput
          name="groupType"
          value={values.groupType}
          error={errors.groupType != null}
          helperText={errors.groupType}
          onChange={handleChange}
          label="Grup Tipi"
          as={Select}
        >
          <option value="" disabled>
            Marka Seçiniz
          </option>
          {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </FormControlFormInput>
        <FormControlFormInput
          name="name"
          value={values.name}
          error={errors.name != null}
          helperText={errors.name}
          onChange={handleChange}
          label="Ad"
        />
        <FormControlFormInput
          name="price"
          value={values.price}
          error={errors.price != null}
          helperText={errors.price}
          onChange={handleChange}
          label="Fiyat"
          type="number"
        />
        {abilities.map((ability) => {
          return (
            <Checkbox
              key={`apaf.abilityDefinitionTypes.${ability.abilityDefinitionTypeId}`}
              colorScheme="blue"
              onChange={(event) => {
                handleAbilityCheckboxChange(ability.abilityDefinitionTypeId, event.target.checked);
              }}
              isChecked={values.abilityDefinitionTypes[ability.abilityDefinitionTypeId]}
            >
              {ability.name}
            </Checkbox>
          );
        })}
        <Divider />

        <Checkbox
          colorScheme="blue"
          onChange={(event) => {
            setFieldValue('includeInRoyalty', event.target.checked);
          }}
          isChecked={values.includeInRoyalty}
        >
          Royalty hesaplanmasın
        </Checkbox>

        <Divider />

        {!values.includeInRoyalty && (
          <>
            <Checkbox
              colorScheme="blue"
              onChange={handleRoyaltyCheckboxChange}
              isChecked={values.royaltyCheckboxChecked}
            >
              Royalty Belirle
            </Checkbox>

            {values.royaltyCheckboxChecked && (
              <FormControlFormInput
                name="royaltyRate"
                value={values.royaltyRate}
                error={errors.royaltyRate != null}
                helperText={errors.royaltyRate}
                onChange={(e) => setFieldValue('royaltyRate', e.target.value)}
                label="Royalty Bedeli"
                type="number"
              />
            )}
          </>
        )}

        {errors.abilityDefinitionTypes && typeof errors.abilityDefinitionTypes === 'string' && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>{errors.abilityDefinitionTypes}</AlertTitle>
          </Alert>
        )}
      </VStack>
    </>
  );
};
